import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Camera,
  FileUp,
  Link2,
  Pencil,
  Plus,
  TriangleAlert,
  Upload,
  Users,
  X,
} from 'lucide-react';
import {
  useListStudentsQuery,
  useListGroupsQuery,
  useGetStudentQuery,
  useCreateStudentMutation,
  useUploadStudentPhotoMutation,
  useUpdateStudentMutation,
  useBulkImportStudentsMutation,
  useGetSiblingsQuery,
  useListCollectorsQuery,
  useCreateCollectorMutation,
  useUpdateCollectorMutation,
  useCopyCollectorsFromMutation,
  useDeactivateCollectorMutation,
  useListDelegatesQuery,
  useAddDelegateMutation,
  useCancelDelegateMutation,
  getApiError,
  type StudentSummary,
  type Group,
} from '@/store/api';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { Mark, studentStatusTone } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Field';
import { SearchField } from '@/components/ui/SearchField';
import { Pagination } from '@/components/ui/Pagination';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

const studentSchema = z.object({
  fullName: z.string().min(2, 'Full name required'),
  dateOfBirth: z.string().min(1, 'Date of birth required'),
  /* Required — a child at a school is in a class, and one without falls
     off every register and every mark sheet. */
  groupId: z.string().min(1, 'Pick the class this child is in'),
  primaryCollectorPhone: z
    .string()
    .regex(/^\+\d{10,15}$/, 'Enter the phone in E.164 format, e.g. +233501234567'),
  status: z.enum(['active', 'inactive', 'graduated']).default('active'),
  studentCode: z
    .string()
    .max(20)
    .regex(/^[A-Za-z0-9\-_]*$/, 'Letters, numbers and hyphens only')
    .optional(),
});
type StudentForm = z.infer<typeof studentSchema>;

const PAGE_SIZE = 20;
const e164 = /^\+\d{10,15}$/;

/*
  An inset panel: the ground for a form that opens INSIDE another surface —
  editing a student within the detail dialog, adding a collector within the
  list of them.

  These used to be `bg-brand-50 border-brand-200`, a tinted indigo ground that
  appeared nowhere else in the product and read as a highlight rather than as a
  nested surface. `bg-muted` is the token that already means "one step back from
  the card", which is what a form nested in a card is.
*/
const INSET = 'rounded-lg border border-border bg-muted/60 p-3.5';

const ERROR_NOTE =
  'flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] ' +
  'px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]';

/** The student code, wherever it appears — one look, monospaced and spaced out,
 *  because it is read aloud and typed at a gate under time pressure. */
function CodeChip({ code }: { code?: string }) {
  if (!code) return <span className="text-muted-foreground opacity-50">—</span>;
  return (
    <span className="rounded-sm border border-border bg-muted px-1.5 py-[2px] font-mono text-[11px] font-bold tracking-[0.12em] text-foreground">
      {code}
    </span>
  );
}

export function StudentsPage() {
  const institutionId = useAuthStore((s) => s.user?.institutionId ?? '');

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentSummary | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [bulkResult, setBulkResult] = useState<{
    created: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [showBulkModal, setShowBulkModal] = useState(false);

  const { data, isLoading, isError, error } = useListStudentsQuery(
    {
      institutionId,
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
      status: statusFilter || undefined,
      groupId: groupFilter || undefined,
    },
    { skip: !institutionId },
  );

  const { data: groups = [] } = useListGroupsQuery(institutionId, { skip: !institutionId });

  const { data: studentDetail } = useGetStudentQuery(
    { institutionId, studentId: selectedStudent?._id ?? '' },
    { skip: !selectedStudent || !showDetailModal },
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StudentForm>({ resolver: zodResolver(studentSchema) });

  const [editingStudent, setEditingStudent] = useState(false);
  const [editStudentError, setEditStudentError] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');

  const [createStudent, createMutation] = useCreateStudentMutation();
  const [uploadStudentPhoto] = useUploadStudentPhotoMutation();
  const [updateStudent, updateMutation] = useUpdateStudentMutation();
  const [bulkImport, bulkMutation] = useBulkImportStudentsMutation();

  const closeAdd = () => {
    setShowAddModal(false);
    reset();
  };

  const onSubmit = async (formData: StudentForm) => {
    try {
      await createStudent({
        institutionId,
        data: formData,
      }).unwrap();
      closeAdd();
    } catch {
      /* Surfaced inline; the dialog stays open so nothing typed is lost. */
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedStudent) return;
    setUploadingPhoto(true);
    setPhotoUploadError('');
    try {
      await uploadStudentPhoto({ institutionId, studentId: selectedStudent._id, file }).unwrap();
    } catch (err) {
      setPhotoUploadError(getApiError(err));
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleBulkImport = async () => {
    if (!bulkFile) return;
    const text = await bulkFile.text();
    const lines = text.split('\n').filter(Boolean);
    const rows = lines.slice(1).map((line) => {
      const [fullName = '', dateOfBirth = '', groupName = '', primaryCollectorPhone = ''] = line
        .split(',')
        .map((c) => c.trim());
      return { fullName, dateOfBirth, groupName, primaryCollectorPhone };
    });
    try {
      setBulkResult(await bulkImport({ institutionId, rows }).unwrap());
    } catch {
      /* Surfaced from bulkMutation.error. */
    }
  };

  const closeBulk = () => {
    setShowBulkModal(false);
    setBulkFile(null);
    setBulkResult(null);
  };

  const openStudent = (student: StudentSummary, edit: boolean) => {
    setSelectedStudent(student);
    setEditingStudent(edit);
    setEditStudentError('');
    setPhotoUploadError('');
    setShowDetailModal(true);
  };

  const hasFilters = Boolean(search || statusFilter || groupFilter);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setGroupFilter('');
    setPage(1);
  };

  const columns: Column<StudentSummary>[] = [
    {
      key: 'fullName',
      header: 'Student',
      render: (s) => (
        <div className="flex items-center gap-2.5">
          <span
            className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[10px] font-bold text-muted-foreground"
            aria-hidden
          >
            {s.fullName[0]?.toUpperCase()}
          </span>
          <span className="font-semibold text-foreground">{s.fullName}</span>
        </div>
      ),
    },
    {
      key: 'studentCode',
      header: 'Code',
      render: (s) => <CodeChip code={s.studentCode} />,
      className: 'w-[110px]',
    },
    {
      key: 'class',
      header: 'Class',
      render: (s) => <span className="text-muted-foreground">{s.class}</span>,
      className: 'w-[140px]',
    },
    {
      key: 'dateOfBirth',
      header: 'Date of birth',
      render: (s) => (
        <span className="tabular-nums text-muted-foreground">{s.dateOfBirth}</span>
      ),
      className: 'w-[120px] max-lg:hidden',
    },
    {
      key: 'primaryCollectorPhone',
      header: 'Parent phone',
      render: (s) => (
        <span className="font-mono text-[11px] text-muted-foreground">{s.primaryCollectorPhone}</span>
      ),
      className: 'max-md:hidden',
    },
    {
      key: 'status',
      header: 'Status',
      render: (s) => <Mark tone={studentStatusTone(s.status)}>{s.status}</Mark>,
      className: 'w-[100px]',
    },
    {
      key: 'actions',
      header: '',
      align: 'num',
      render: (s) => (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label={`Edit ${s.fullName}`}
          icon={<Pencil size={12} />}
          onClick={(e) => {
            e.stopPropagation();
            openStudent(s, true);
          }}
        />
      ),
      className: 'w-[56px]',
    },
  ];

  return (
    <PageLayout
      title="Students"
      subtitle="The register the gate verifies against"
      breadcrumbs={[{ label: 'Register' }, { label: 'Students' }]}
      meta={
        data?.total !== undefined ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {data.total} enrolled
          </span>
        ) : undefined
      }
      actions={
        <>
          <Button variant="secondary" icon={<FileUp size={13} />} onClick={() => setShowBulkModal(true)}>
            Bulk import
          </Button>
          <Button variant="primary" icon={<Plus size={13} />} onClick={() => setShowAddModal(true)}>
            Add student
          </Button>
        </>
      }
      toolbar={
        <>
          <SearchField
            value={search}
            onChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            placeholder="Search by name or code…"
            className="w-full max-w-[260px]"
          />
          <Select
            aria-label="Filter by group"
            className="w-[170px]"
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All groups</option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </Select>
          <Select
            aria-label="Filter by status"
            className="w-[140px]"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="graduated">Graduated</option>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </>
      }
    >
      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(s) => s._id}
        loading={isLoading}
        error={isError || undefined}
        errorMessage={isError ? getApiError(error) : undefined}
        onRowClick={(s) => openStudent(s, false)}
        emptyTitle={hasFilters ? 'No students match this search' : 'No students yet'}
        emptyDescription={
          hasFilters
            ? 'Clear the filters to see the whole register.'
            : 'Add students one at a time, or import a CSV of the whole school.'
        }
        emptyAction={
          hasFilters ? (
            <Button variant="secondary" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : (
            <Button variant="primary" icon={<Plus size={13} />} onClick={() => setShowAddModal(true)}>
              Add the first student
            </Button>
          )
        }
        footer={
          (data?.totalPages ?? 1) > 1 ? (
            <Pagination
              page={page}
              totalPages={data!.totalPages}
              total={data!.total}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          ) : undefined
        }
      />

      {/* ── Add student ──────────────────────────────────────────────────── */}
      <Modal
        open={showAddModal}
        onClose={closeAdd}
        title="Add student"
        description="They appear on the gate register from the next code generation."
        footer={
          <>
            <Button variant="secondary" onClick={closeAdd}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="add-student" loading={createMutation.isLoading}>
              Add student
            </Button>
          </>
        }
      >
        <form id="add-student" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3" noValidate>
          <Field label="Full name" htmlFor="s-name" error={errors.fullName?.message} required>
            <Input id="s-name" placeholder="Ama Mensah" invalid={!!errors.fullName} {...register('fullName')} />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Date of birth" htmlFor="s-dob" error={errors.dateOfBirth?.message} required>
              <Input id="s-dob" type="date" invalid={!!errors.dateOfBirth} {...register('dateOfBirth')} />
            </Field>
            <Field
              label="Class"
              htmlFor="s-group"
              error={errors.groupId?.message}
              required
              hint={groups.length === 0 ? 'No classes exist yet — create one under Groups first.' : undefined}
            >
              <Select id="s-group" {...register('groupId')}>
                <option value="">Pick a class…</option>
                {groups.map((g) => (
                  <option key={g._id} value={g._id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <Field
            label="Parent / guardian phone"
            htmlFor="s-phone"
            error={errors.primaryCollectorPhone?.message}
            hint="This number receives the daily pickup code by SMS."
            required
          >
            <Input
              id="s-phone"
              placeholder="+233501234567"
              invalid={!!errors.primaryCollectorPhone}
              {...register('primaryCollectorPhone')}
            />
          </Field>

          <Field
            label="Student code"
            htmlFor="s-code"
            error={errors.studentCode?.message}
            hint="Optional — used for manual lookup at the gate. Auto-generated if left blank."
          >
            <Input
              id="s-code"
              className="font-mono uppercase tracking-[0.12em]"
              placeholder="STU001"
              invalid={!!errors.studentCode}
              {...register('studentCode')}
            />
          </Field>

          {createMutation.error && (
            <div role="alert" className={ERROR_NOTE}>
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {getApiError(createMutation.error)}
            </div>
          )}
        </form>
      </Modal>

      {/* ── Student detail ───────────────────────────────────────────────── */}
      <Modal
        open={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setEditingStudent(false);
          setEditStudentError('');
          setPhotoUploadError('');
        }}
        title={selectedStudent?.fullName ?? 'Student'}
        description={selectedStudent?.class}
        size="lg"
      >
        {studentDetail ? (
          <div className="flex flex-col gap-4">
            {/* Photo — what a gate officer checks the collector against. */}
            <div className="flex items-start gap-3.5">
              <Avatar
                src={studentDetail.photoUrl}
                name={studentDetail.fullName as string}
                size={72}
                zoomable
                caption={studentDetail.class as string | undefined}
              />
              <div className="min-w-0">
                <p className={styles.label}>Gate photo</p>
                <p className={cn(styles.meta, 'mt-1 max-w-[320px]')}>
                  Shown to the officer when this child's code is verified.
                </p>
                <label
                  htmlFor="student-photo-upload"
                  className={cn(styles.btnSecondary, styles.btnSizes.sm, 'mt-2 cursor-pointer')}
                >
                  {uploadingPhoto ? <Spinner size="sm" /> : <Camera size={12} />}
                  {uploadingPhoto ? 'Uploading…' : studentDetail.photoUrl ? 'Change photo' : 'Upload photo'}
                </label>
                <input
                  id="student-photo-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="sr-only"
                  onChange={handlePhotoUpload}
                  disabled={uploadingPhoto}
                />
                {photoUploadError && (
                  <div role="alert" className={cn(ERROR_NOTE, 'mt-2')}>
                    <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                    {photoUploadError}
                  </div>
                )}
              </div>
            </div>

            {editingStudent ? (
              <StudentEditForm
                student={studentDetail as StudentSummary & { collectors?: unknown[] }}
                groups={groups}
                onSave={async (patch) => {
                  setEditStudentError('');
                  try {
                    await updateStudent({
                      institutionId,
                      studentId: selectedStudent!._id,
                      data: patch,
                    }).unwrap();
                    setEditingStudent(false);
                  } catch (err) {
                    setEditStudentError(getApiError(err));
                  }
                }}
                onCancel={() => {
                  setEditingStudent(false);
                  setEditStudentError('');
                }}
                saving={updateMutation.isLoading}
                saveError={editStudentError}
              />
            ) : (
              <div className={INSET}>
                <dl className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
                  <DetailRow label="Code" value={<CodeChip code={(studentDetail as unknown as { studentCode?: string }).studentCode} />} />
                  <DetailRow label="Group" value={(studentDetail.class as string) || '—'} />
                  <DetailRow label="Date of birth" value={studentDetail.dateOfBirth as string} mono />
                  <DetailRow label="Parent phone" value={studentDetail.primaryCollectorPhone as string} mono />
                  <DetailRow
                    label="Status"
                    value={
                      <Mark tone={studentStatusTone(studentDetail.status as string)}>
                        {studentDetail.status as string}
                      </Mark>
                    }
                  />
                </dl>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<Pencil size={12} />}
                  className="mt-3"
                  onClick={() => setEditingStudent(true)}
                >
                  Edit details
                </Button>
              </div>
            )}

            <Section title="Authorised collectors" hint="Anyone here may collect this child with the daily code.">
              <StudentCollectors studentId={selectedStudent?._id ?? ''} institutionId={institutionId} />
            </Section>

            <Section title="Active delegates" hint="One-off or recurring permissions on top of the collectors above.">
              <StudentDelegates studentId={selectedStudent?._id ?? ''} />
            </Section>
          </div>
        ) : (
          <div className="flex h-40 items-center justify-center">
            <Spinner size="lg" />
          </div>
        )}
      </Modal>

      {/* ── Bulk import ──────────────────────────────────────────────────── */}
      <Modal
        open={showBulkModal}
        onClose={closeBulk}
        title="Bulk import students"
        description="One row per student. The header row is skipped."
        footer={
          <>
            <Button variant="secondary" onClick={closeBulk}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => void handleBulkImport()}
              disabled={!bulkFile}
              loading={bulkMutation.isLoading}
              icon={<Upload size={13} />}
            >
              Import
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <div className={INSET}>
            <p className={styles.label}>Required columns, in order</p>
            <code className="mt-1.5 block font-mono text-[11px] leading-relaxed text-foreground">
              fullName, dateOfBirth, groupName, primaryCollectorPhone
            </code>
            <p className="mt-2 text-[11px] leading-relaxed text-[hsl(var(--record-ink-warning))]">
              <span className="font-mono font-semibold">groupName</span> must match a group you have
              already created, exactly. Rows whose group does not match are reported below rather than
              guessed at.
            </p>
          </div>

          <input
            type="file"
            accept=".csv"
            id="csv-upload"
            className="sr-only"
            onChange={(e) => setBulkFile(e.target.files?.[0] ?? null)}
          />
          <label
            htmlFor="csv-upload"
            className="flex cursor-pointer flex-col items-center gap-1.5 rounded-lg border border-dashed border-border px-6 py-7 text-center transition-colors hover:border-primary/50 hover:bg-accent/40"
          >
            <FileUp size={19} className="text-muted-foreground" />
            <span className="text-[12px] font-semibold text-foreground">
              {bulkFile ? bulkFile.name : 'Choose a CSV file'}
            </span>
            <span className="text-[10.5px] text-muted-foreground">
              {bulkFile ? `${(bulkFile.size / 1024).toFixed(0)} KB — click to replace` : 'Up to 10 MB'}
            </span>
          </label>

          {bulkResult && (
            <div
              className={cn(
                'rounded-lg border px-3 py-2.5',
                bulkResult.errors.length > 0
                  ? 'border-warning/30 bg-warning/[0.08]'
                  : 'border-success/25 bg-success/[0.07]',
              )}
            >
              <p
                className={cn(
                  'text-[12px] font-bold',
                  bulkResult.errors.length > 0
                    ? 'text-[hsl(var(--record-ink-warning))]'
                    : 'text-[hsl(var(--record-ink-success))]',
                )}
              >
                {bulkResult.created} imported
                {bulkResult.errors.length > 0 && `, ${bulkResult.errors.length} skipped`}
              </p>
              {bulkResult.errors.length > 0 && (
                <ul className="mt-1.5 flex max-h-40 flex-col gap-0.5 overflow-y-auto">
                  {bulkResult.errors.map((e) => (
                    <li key={e.row} className="text-[10.5px] text-[hsl(var(--record-ink-danger))]">
                      <span className="font-mono font-semibold">Row {e.row}</span> — {e.message}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {bulkMutation.error && (
            <div role="alert" className={ERROR_NOTE}>
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {getApiError(bulkMutation.error)}
            </div>
          )}
        </div>
      </Modal>
    </PageLayout>
  );
}

/* ── Small shared pieces ───────────────────────────────────────────────────── */

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className={styles.label}>{label}</dt>
      <dd className={cn('mt-0.5 text-[12px] font-semibold text-foreground', mono && 'font-mono')}>
        {value}
      </dd>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-t border-border pt-3.5">
      <h3 className={styles.sectionTitle}>{title}</h3>
      {hint && <p className={cn(styles.meta, 'mt-0.5')}>{hint}</p>}
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/* ── Student edit ──────────────────────────────────────────────────────────── */

type StudentPatch = {
  fullName?: string;
  dateOfBirth?: string;
  groupId?: string | null;
  primaryCollectorPhone?: string;
  status?: string;
  studentCode?: string;
};

function StudentEditForm({
  student,
  groups,
  onSave,
  onCancel,
  saving,
  saveError,
}: {
  student: StudentSummary & { collectors?: unknown[] };
  groups: Group[];
  onSave: (patch: StudentPatch) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
  saveError: string;
}) {
  const [local, setLocal] = useState({
    fullName: student.fullName ?? '',
    dateOfBirth: student.dateOfBirth ?? '',
    groupId: student.groupId ?? '',
    primaryCollectorPhone: student.primaryCollectorPhone ?? '',
    status: student.status ?? 'active',
    studentCode: (student as unknown as { studentCode?: string }).studentCode ?? '',
  });
  const [validationError, setValidationError] = useState('');

  const set = <K extends keyof typeof local>(k: K, v: (typeof local)[K]) =>
    setLocal((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (local.fullName.trim().length < 2) {
      setValidationError('Full name must be at least 2 characters.');
      return;
    }
    if (!local.dateOfBirth) {
      setValidationError('Date of birth is required.');
      return;
    }
    if (!e164.test(local.primaryCollectorPhone)) {
      setValidationError('Phone must be in E.164 format, e.g. +233501234567');
      return;
    }
    setValidationError('');
    await onSave({
      fullName: local.fullName.trim(),
      dateOfBirth: local.dateOfBirth,
      groupId: local.groupId || undefined,
      primaryCollectorPhone: local.primaryCollectorPhone,
      status: local.status,
      studentCode: local.studentCode.trim().toUpperCase() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className={cn(INSET, 'flex flex-col gap-3')} noValidate>
      <p className={styles.label}>Edit student details</p>

      <Field label="Full name">
        <Input value={local.fullName} onChange={(e) => set('fullName', e.target.value)} />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Date of birth">
          <Input type="date" value={local.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
        </Field>
        <Field label="Class" required>
          <Select value={local.groupId ?? ''} onChange={(e) => set('groupId', e.target.value)}>
            {/* No "none" option: a child can be MOVED between classes, never
                taken out of all of them. */}
            <option value="" disabled>
              Pick a class…
            </option>
            {groups.map((g) => (
              <option key={g._id} value={g._id}>
                {g.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Parent / guardian phone">
          <Input
            placeholder="+233501234567"
            value={local.primaryCollectorPhone}
            onChange={(e) => set('primaryCollectorPhone', e.target.value)}
          />
        </Field>
        <Field label="Status">
          <Select value={local.status} onChange={(e) => set('status', e.target.value)}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="graduated">Graduated</option>
          </Select>
        </Field>
      </div>

      <Field label="Student code" hint="Used for manual gate lookup. Leave blank to keep the existing one.">
        <Input
          className="font-mono uppercase tracking-[0.12em]"
          placeholder="STU001"
          value={local.studentCode}
          onChange={(e) => set('studentCode', e.target.value.toUpperCase())}
        />
      </Field>

      {(validationError || saveError) && (
        <div role="alert" className={ERROR_NOTE}>
          <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
          {validationError || saveError}
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" loading={saving}>
          Save changes
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/* ── Collectors ────────────────────────────────────────────────────────────── */

type EditState = {
  fullName: string;
  relationship: string;
  phoneE164: string;
  additionalPhones: string[];
  idType: string;
};

function PhoneList({
  phones,
  onChange,
}: {
  phones: string[];
  onChange: (phones: string[]) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {phones.map((p, i) => (
        <div key={i} className="flex items-center gap-1.5">
          <Input
            className="flex-1"
            placeholder="+233501234567"
            value={p}
            aria-label={`Additional phone ${i + 1}`}
            onChange={(e) => onChange(phones.map((v, idx) => (idx === i ? e.target.value : v)))}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            aria-label={`Remove phone ${i + 1}`}
            icon={<X size={12} />}
            className="text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
            onClick={() => onChange(phones.filter((_, idx) => idx !== i))}
          />
        </div>
      ))}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon={<Plus size={12} />}
        className="self-start"
        onClick={() => onChange([...phones, ''])}
      >
        Add another number
      </Button>
    </div>
  );
}

function CollectorForm({
  defaultValues,
  onSave,
  onCancel,
  saving,
  saveError,
  submitLabel,
}: {
  defaultValues: EditState & { isPrimary?: boolean };
  onSave: (data: EditState & { isPrimary?: boolean }) => void;
  onCancel: () => void;
  saving: boolean;
  saveError?: string;
  submitLabel: string;
}) {
  const [local, setLocal] = useState(defaultValues);
  const [validationError, setValidationError] = useState('');
  const set = (k: keyof typeof local, v: unknown) => setLocal((s) => ({ ...s, [k]: v }));

  const validate = (): string | null => {
    if (local.fullName.trim().length < 2) return 'Name is required.';
    if (!local.relationship) return 'Select a relationship.';
    if (!e164.test(local.phoneE164)) return 'Primary phone must be E.164 format, e.g. +233501234567';
    for (const p of local.additionalPhones) {
      if (p && !e164.test(p)) return `"${p}" is not a valid E.164 number.`;
    }
    if (!local.idType) return 'Select an ID type.';
    return null;
  };

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault();
    const err = validate();
    if (err) {
      setValidationError(err);
      return;
    }
    setValidationError('');
    onSave({ ...local, additionalPhones: local.additionalPhones.filter(Boolean) });
  };

  return (
    <form onSubmit={handleSubmit} className={cn(INSET, 'flex flex-col gap-3')} noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Full name">
          <Input placeholder="Ama Mensah" value={local.fullName} onChange={(e) => set('fullName', e.target.value)} />
        </Field>
        <Field label="Relationship">
          <Select value={local.relationship} onChange={(e) => set('relationship', e.target.value)}>
            <option value="">Select…</option>
            <option value="parent">Parent</option>
            <option value="guardian">Guardian</option>
            <option value="grandparent">Grandparent</option>
            <option value="sibling">Sibling</option>
            <option value="driver">Driver</option>
            <option value="relative">Relative</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="ID type">
          <Select value={local.idType} onChange={(e) => set('idType', e.target.value)}>
            <option value="">Select…</option>
            <option value="ghana_card">Ghana Card</option>
            <option value="passport">Passport</option>
            <option value="voter_id">Voter's ID</option>
            <option value="drivers_license">Driver's licence</option>
          </Select>
        </Field>
        <Field label="Primary phone">
          <Input placeholder="+233501234567" value={local.phoneE164} onChange={(e) => set('phoneE164', e.target.value)} />
        </Field>
      </div>

      <Field
        label="Additional phone numbers"
        hint="SMS goes to all of them, and each can sign in to the parent app."
      >
        <PhoneList phones={local.additionalPhones} onChange={(phones) => set('additionalPhones', phones)} />
      </Field>

      {'isPrimary' in defaultValues && (
        <label className="flex items-center gap-2 text-[11.5px] font-semibold text-foreground">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={Boolean(local.isPrimary)}
            onChange={(e) => set('isPrimary', e.target.checked)}
          />
          Set as primary collector
        </label>
      )}

      {(validationError || saveError) && (
        <div role="alert" className={ERROR_NOTE}>
          <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
          {validationError || saveError}
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" size="sm" loading={saving}>
          {submitLabel}
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function StudentCollectors({ studentId, institutionId }: { studentId: string; institutionId: string }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editError, setEditError] = useState('');
  const [addError, setAddError] = useState('');
  const [copyError, setCopyError] = useState('');

  const { data: collectors = [], isLoading } = useListCollectorsQuery(studentId, { skip: !studentId });
  const { data: siblings = [] } = useGetSiblingsQuery(
    { institutionId, studentId },
    { skip: !studentId || !institutionId },
  );

  const [createCollector, addMutation] = useCreateCollectorMutation();
  const [updateCollector, updateMutation] = useUpdateCollectorMutation();
  const [deactivateCollector, deactivateMutation] = useDeactivateCollectorMutation();
  const [copyFrom, copyMutation] = useCopyCollectorsFromMutation();

  const nonPrimaryActive = collectors.filter((c) => !c.isPrimary && c.isActive);
  const showSiblingBanner = siblings.length > 0 && nonPrimaryActive.length === 0 && !showAdd && !editingId;

  const handleCopyFrom = async (sourceStudentId: string) => {
    setCopyError('');
    try {
      const copied = await copyFrom({ studentId, sourceStudentId }).unwrap();
      if (copied.length === 0) setCopyError('That sibling has no secondary collectors to copy.');
    } catch (err) {
      setCopyError(getApiError(err));
    }
  };

  const handleAdd = async (data: EditState & { isPrimary?: boolean }) => {
    setAddError('');
    try {
      await createCollector({ studentId, data: { ...data, isPrimary: data.isPrimary ?? false } }).unwrap();
      setShowAdd(false);
    } catch (err) {
      setAddError(getApiError(err));
    }
  };

  const handleEdit = async (data: EditState) => {
    if (!editingId) return;
    setEditError('');
    try {
      await updateCollector({ studentId, collectorId: editingId, data }).unwrap();
      setEditingId(null);
    } catch (err) {
      setEditError(getApiError(err));
    }
  };

  if (isLoading) return <Spinner size="sm" />;

  return (
    <div className="flex flex-col gap-2">
      {/*
        Siblings share a parent phone, so the second child enrolled almost always
        wants the first child's collectors. Offering the copy is the difference
        between one click and re-typing four people with their ID types.
      */}
      {showSiblingBanner && (
        <div className="rounded-lg border border-info/25 bg-info/[0.07] px-3 py-2.5">
          <p className="flex items-center gap-1.5 text-[11.5px] font-semibold text-[hsl(var(--record-ink-info))]">
            <Link2 size={12} />
            Shares a parent phone with another student
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {siblings.map((s) => (
              <Button
                key={s._id}
                variant="secondary"
                size="sm"
                loading={copyMutation.isLoading}
                onClick={() => void handleCopyFrom(s._id)}
              >
                Copy collectors from {s.fullName}
              </Button>
            ))}
          </div>
          {copyError && (
            <p className="mt-1.5 text-[10.5px] font-medium text-[hsl(var(--record-ink-danger))]">{copyError}</p>
          )}
        </div>
      )}

      {collectors.length === 0 && !showAdd && !showSiblingBanner && (
        <p className="flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
          <Users size={12} />
          No collectors on file yet.
        </p>
      )}

      {collectors.map((c) =>
        editingId === c._id ? (
          <CollectorForm
            key={c._id}
            defaultValues={{
              fullName: c.fullName,
              relationship: c.relationship,
              phoneE164: c.phoneE164,
              additionalPhones: c.additionalPhones ?? [],
              idType: c.idType,
            }}
            onSave={handleEdit}
            onCancel={() => {
              setEditingId(null);
              setEditError('');
            }}
            saving={updateMutation.isLoading}
            saveError={editError}
            submitLabel="Save changes"
          />
        ) : (
          <div
            key={c._id}
            className={cn(
              'rounded-lg border border-border bg-card px-3 py-2.5',
              !c.isActive && 'opacity-60',
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[12px] font-semibold text-foreground">{c.fullName}</span>
                  <span className="text-[11px] text-muted-foreground">· {c.relationship}</span>
                  {c.isPrimary && <Mark tone="info">Primary</Mark>}
                  {!c.isActive && <Mark tone="neutral">Removed</Mark>}
                </div>
                <div className="mt-1 flex flex-wrap gap-x-3">
                  <span className="font-mono text-[10.5px] text-muted-foreground">{c.phoneE164}</span>
                  {(c.additionalPhones ?? []).map((p) => (
                    <span key={p} className="font-mono text-[10.5px] text-muted-foreground/70">
                      {p}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1">
                {c.isActive ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={`Edit ${c.fullName}`}
                      icon={<Pencil size={12} />}
                      onClick={() => {
                        setEditingId(c._id);
                        setEditError('');
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={deactivateMutation.isLoading}
                      className="text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
                      onClick={() => void deactivateCollector({ studentId, collectorId: c._id })}
                    >
                      Remove
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[hsl(var(--record-ink-success))] hover:bg-success/10"
                    onClick={() =>
                      void updateCollector({ studentId, collectorId: c._id, data: { isActive: true } })
                    }
                  >
                    Restore
                  </Button>
                )}
              </div>
            </div>
          </div>
        ),
      )}

      {showAdd ? (
        <CollectorForm
          defaultValues={{
            fullName: '',
            relationship: '',
            phoneE164: '',
            additionalPhones: [],
            idType: '',
            isPrimary: false,
          }}
          onSave={handleAdd}
          onCancel={() => {
            setShowAdd(false);
            setAddError('');
          }}
          saving={addMutation.isLoading}
          saveError={addError}
          submitLabel="Save collector"
        />
      ) : (
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus size={12} />}
          className="self-start"
          onClick={() => setShowAdd(true)}
        >
          Add collector
        </Button>
      )}
    </div>
  );
}

/* ── Delegates ─────────────────────────────────────────────────────────────── */

const delegateSchema = z.object({
  fullName: z.string().min(2, 'Name required'),
  phoneE164: z.string().regex(/^\+\d{10,15}$/, 'E.164 format, e.g. +233501234567'),
  relationship: z.string().min(1, 'Relationship required'),
  type: z.enum(['recurring', 'one_day', 'scheduled']),
  validDate: z.string().optional(),
});
type DelegateForm = z.infer<typeof delegateSchema>;

function StudentDelegates({ studentId }: { studentId: string }) {
  const [showAdd, setShowAdd] = useState(false);
  const { data: delegates = [], isLoading } = useListDelegatesQuery(studentId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<DelegateForm>({
    resolver: zodResolver(delegateSchema),
    defaultValues: { type: 'one_day' },
  });
  const delegateType = watch('type');

  const [addDelegate, addMutation] = useAddDelegateMutation();
  const [cancelDelegate, cancelMutation] = useCancelDelegateMutation();

  const onAddDelegate = async (data: DelegateForm) => {
    try {
      await addDelegate({ studentId, data }).unwrap();
      setShowAdd(false);
      reset();
    } catch {
      /* Surfaced inline. */
    }
  };

  if (isLoading) return <Spinner size="sm" />;

  const active = delegates.filter((d) => d.isActive);

  return (
    <div className="flex flex-col gap-2">
      {active.length === 0 && !showAdd && (
        <p className="text-[11.5px] text-muted-foreground">No active delegates.</p>
      )}

      {/* Field names match what the API actually sends — see DelegateSummary.
          `relationship` is accepted on create but is not returned, so it cannot
          be shown here. */}
      {active.map((d) => (
        <div
          key={d.id}
          className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-3 py-2"
        >
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="text-[12px] font-semibold text-foreground">{d.name}</span>
            <Mark tone={d.isRecurring ? 'info' : 'warning'}>
              {d.isRecurring ? 'Recurring' : d.validDate ? 'Scheduled' : 'One day'}
            </Mark>
            {d.validDate && (
              <span className="text-[10.5px] tabular-nums text-muted-foreground">{d.validDate}</span>
            )}
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <span className="font-mono text-[10.5px] text-muted-foreground">{d.phone}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={cancelMutation.isLoading}
              className="text-[hsl(var(--record-ink-danger))] hover:bg-destructive/10"
              onClick={() => void cancelDelegate({ studentId, delegateId: d.id })}
            >
              Cancel
            </Button>
          </div>
        </div>
      ))}

      {showAdd ? (
        <form
          onSubmit={handleSubmit(onAddDelegate)}
          className={cn(INSET, 'flex flex-col gap-3')}
          noValidate
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Full name" error={errors.fullName?.message}>
              <Input placeholder="Kofi Boateng" invalid={!!errors.fullName} {...register('fullName')} />
            </Field>
            <Field label="Phone" error={errors.phoneE164?.message}>
              <Input placeholder="+233501234567" invalid={!!errors.phoneE164} {...register('phoneE164')} />
            </Field>
            <Field label="Relationship" error={errors.relationship?.message}>
              <Input
                placeholder="Uncle, aunt, sibling…"
                invalid={!!errors.relationship}
                {...register('relationship')}
              />
            </Field>
            <Field label="Type">
              <Select {...register('type')}>
                <option value="one_day">One day only</option>
                <option value="recurring">Recurring</option>
                <option value="scheduled">Scheduled date</option>
              </Select>
            </Field>
            {delegateType === 'scheduled' && (
              <Field label="Valid date" className="sm:col-span-2">
                <Input type="date" {...register('validDate')} />
              </Field>
            )}
          </div>

          {addMutation.error && (
            <div role="alert" className={ERROR_NOTE}>
              <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
              {getApiError(addMutation.error)}
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" size="sm" loading={addMutation.isLoading}>
              Save delegate
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowAdd(false);
                reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          icon={<Plus size={12} />}
          className="self-start"
          onClick={() => setShowAdd(true)}
        >
          Add delegate
        </Button>
      )}
    </div>
  );
}
